import { Link } from "react-router-dom";

import { socialMediaLinks } from "simplydo/core/users";
import {
  BRAND_ICONS,
  LinkedInIcon,
  MSTeamsIcon,
} from "simplydo/react/ui/brand-icon";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import { useAppSelector } from "store";

import type { ChipUser, SessionUser } from "./types";

const PROFILE_SOCIAL_KEYS = [
  "linkedin",
  "website",
  "facebook",
  "instagram",
  "github",
  "twitter",
] as const;

type ProfileSocialKey = (typeof PROFILE_SOCIAL_KEYS)[number];

type SocialLinksProps = { user: ChipUser };

export const SocialLinks = ({ user: userProp }: SocialLinksProps) => {
  const currentUser = useAppSelector(
    (state) => state.user,
  ) as SessionUser | null;

  let user: ChipUser = userProp;
  const hasProfile = user?.profile;
  if (!hasProfile) {
    user = { profile: { fullName: "Unknown User" } } as ChipUser;
  }

  const org = currentUser?.ownerOrganisation;

  const profileSocialElements = (
    hasProfile
      ? PROFILE_SOCIAL_KEYS.filter((key) => !!user?.profile?.[key])
      : []
  ) as ProfileSocialKey[];

  const teamsHandle = user?.teamsHandle;
  const canMessageViaTeams =
    !!currentUser?.ownerOrganisation?.enabledFeatures?.includes(
      "teamsHandleAdvertising",
    ) && !!teamsHandle;

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 5,
        gap: 4,
      }}
    >
      {!user?.profile?.linkedin &&
        currentUser?._id === user._id &&
        org?.superadminTags?.innovationType === "externalInnovation" && (
          <Popup
            offset={[-10, 2]}
            content={<p>Add your LinkedIn</p>}
            trigger={
              <Link
                to="/preferences/profile"
                onClick={(e) => e.stopPropagation()}
              >
                <Icon
                  icon={LinkedInIcon}
                  size="small"
                  style={{ color: "#8a8e90ff" }}
                />
              </Link>
            }
          />
        )}

      {profileSocialElements.map((socialElement) => {
        const link = user?.profile?.[socialElement];
        if (!link) return null;
        const socialConfig = socialMediaLinks[socialElement];
        const fullLink =
          link.startsWith("https://") || link.startsWith("www.")
            ? link
            : socialConfig.link + link;
        const BrandIcon =
          "brand" in socialConfig ? BRAND_ICONS[socialConfig.brand] : null;

        return (
          <a
            key={socialElement}
            href={fullLink}
            target="blank"
            onClick={(e) => e.stopPropagation()}
            rel="noreferrer noopenner"
          >
            {BrandIcon ? (
              <Icon
                icon={BrandIcon}
                size="small"
                style={{ color: socialConfig.color, marginRight: 0 }}
              />
            ) : null}
            {"icon" in socialConfig && socialConfig.icon && (
              <Icon
                style={{
                  color: socialConfig.color,
                  marginRight: 0,
                  marginTop: 2,
                }}
                icon={socialConfig.icon}
                size="small"
              />
            )}
            {"iconLeftAlt" in socialConfig && socialConfig.iconLeftAlt && (
              <span
                style={{
                  fontSize: "1.15em",
                  color: "black",
                  padding: "0 0.1em",
                }}
              >
                {socialConfig.iconLeftAlt}
              </span>
            )}
          </a>
        );
      })}
      {canMessageViaTeams ? (
        <Popup
          content={<p>Message via Teams</p>}
          offset={[-12, 4]}
          trigger={
            <a
              key="teams"
              href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(teamsHandle)}`}
              target="blank"
              onClick={(e) => e.stopPropagation()}
              rel="noreferrer noopenner"
            >
              <Icon
                icon={MSTeamsIcon}
                size="small"
                style={{ color: "#6264A7" }}
              />
            </a>
          }
        />
      ) : null}
    </div>
  );
};
